import { KlageState } from '../../klage/reducer/klage-reducer';
import { SavingState } from '../../reducers/savingState';
import { convertToDate, filterForsikringerBySkadeDato } from '../../sporsmalsflyt/forsikring/forsikring-utils';
import { failureFetchingSporsmal, loadingSporsmalSteg } from '../../sporsmalsflyt/reducer/current-steg';
import { initialSporsmalsflytState, sporsmalsflytReducer } from '../../sporsmalsflyt/reducer/sporsmalsflyt-reducer';
import {
    initializeSporsmalsflytState,
    SporsmalsflytState,
    sporsmalsflytStateIsEmpty,
} from '../../sporsmalsflyt/reducer/sporsmalsflyt-state';
import {
    convertFromSporsmalsflytDTO,
    Sporsmal,
    SporsmalsGruppe,
} from '../../sporsmalsflyt/reducer/sporsmalsflyt-types';
import { DateAsString, MeldSkadeResultWebDTO, Skademeldingstype } from '../../types/generated-types';
import {
    FetchedSporsmalsflytAction,
    FetchingSporsmalsflytAction,
    FetchingSporsmalsflytFailedAction,
    ResetSavingStateAction,
    SaveSkademeldigBadRequestFailureAction,
    SaveSkademeldigUnauthorisedFailureAction,
    SaveSkademeldingAction,
    SaveSkademeldingFailureAction,
    SaveSkademeldingSuccessAction,
    SaveSkademeldingVedleggFailureAction,
    SkademeldingActionKeys,
    UpdateShouldRouteKundeToFremtindAction,
    ValidSessionAction,
} from './skademelding-actions';
import { skademeldingstyperForPersonskade } from './skadetype';

export interface SkademeldingState {
    skadetype?: Skademeldingstype;
    sporsmalsflytState: SporsmalsflytState;
    savingState: SavingState;
    sporsmals: Sporsmal[];
    triggered: SporsmalsGruppe[];
    validSession: boolean;
    skademeldingResult?: MeldSkadeResultWebDTO;
    routeToFremtindFeatureToggleEnabled: boolean;
    shouldRouteKundeToFremtind: boolean;
    fremtindUrl?: string;
    fremtindSkadeforsikringKonvertingStartDate: DateAsString;
}

const initialState: SkademeldingState = {
    skadetype: undefined,
    sporsmals: [],
    triggered: [],
    sporsmalsflytState: initialSporsmalsflytState,
    savingState: SavingState.Initial,
    validSession: true,
    routeToFremtindFeatureToggleEnabled: false,
    shouldRouteKundeToFremtind: false,
    fremtindSkadeforsikringKonvertingStartDate: new Date(2026, 0, 1, 0, 0, 0, 0).toDateString(),
};

type Actions =
    | FetchingSporsmalsflytAction
    | FetchedSporsmalsflytAction
    | FetchingSporsmalsflytFailedAction
    | SaveSkademeldingAction
    | SaveSkademeldingSuccessAction
    | SaveSkademeldingFailureAction
    | SaveSkademeldigUnauthorisedFailureAction
    | SaveSkademeldigBadRequestFailureAction
    | SaveSkademeldingVedleggFailureAction
    | ResetSavingStateAction
    | ValidSessionAction
    | UpdateShouldRouteKundeToFremtindAction
    | never;

export const skademeldingReducer = (
    state: SkademeldingState = initialState,
    action: Actions = {} as Actions,
): SkademeldingState => {
    switch (action.type) {
        case SkademeldingActionKeys.FetchingSporsmalsflyt:
            return {
                ...state,
                sporsmalsflytState: {
                    ...state.sporsmalsflytState,
                    currentSteg: loadingSporsmalSteg(),
                },
                skadetype: action.skadetype,
            };
        case SkademeldingActionKeys.FetchedSporsmalsflyt: {
            const { sporsmalsList, triggered } = convertFromSporsmalsflytDTO(action.sporsmalsflyt);
            return {
                ...state,
                sporsmals: sporsmalsList,
                triggered,
                sporsmalsflytState: initializeSporsmalsflytState(sporsmalsList),
                savingState: SavingState.Initial,
                fremtindSkadeforsikringKonvertingStartDate: action.fremtindSkadeforsikringKonvertingStartDate,
                routeToFremtindFeatureToggleEnabled: action.enableRoutingToFremtind,
                fremtindUrl: action.fremtindUrl,
            };
        }
        case SkademeldingActionKeys.FetchingSporsmalsflytFailed:
            return {
                ...state,
                sporsmalsflytState: {
                    ...state.sporsmalsflytState,
                    currentSteg: failureFetchingSporsmal(),
                },
                sporsmals: [],
            };
        case SkademeldingActionKeys.SaveSkademelding:
            return {
                ...state,
                savingState: SavingState.Saving,
            };
        case SkademeldingActionKeys.SaveSkademeldingSuccess:
            return {
                ...state,
                savingState: SavingState.Success,
                skademeldingResult: action.skademeldingResult,
            };
        case SkademeldingActionKeys.SaveSkademeldingFailure:
            return {
                ...state,
                savingState: SavingState.Failure,
            };
        case SkademeldingActionKeys.SaveSkademeldingUnauthorisedFailure:
            return {
                ...state,
                savingState: SavingState.UnauthorizedUser,
            };
        case SkademeldingActionKeys.SaveSkademeldingBadRequestFailure:
            return {
                ...state,
                savingState: SavingState.BadRequestFailure,
            };
        case SkademeldingActionKeys.ResetSavingState:
            return {
                ...state,
                savingState: SavingState.Initial,
            };
        case SkademeldingActionKeys.SaveSkademeldingVedleggFailure:
            return {
                ...state,
                savingState: SavingState.UgyldigVedlegg,
            };
        case SkademeldingActionKeys.UpdateValidSession: {
            const keepIn = skademeldingstyperForPersonskade().includes(state.skadetype);
            if (keepIn) {
                return {
                    ...state,
                    validSession: true,
                };
            }
            return {
                ...state,
                validSession: false,
            };
        }
        case SkademeldingActionKeys.UpdateShouldRouteKundeToFremtind: {
            const kundeHasNoActiveForsikringOnSkadedato =
                filterForsikringerBySkadeDato(action.forsikringer || [], new Date(action.skadedato)).length === 0;

            const isSkadedatoAfterKonvertingStartDate =
                convertToDate(action.skadedato.toString()) >=
                convertToDate(state.fremtindSkadeforsikringKonvertingStartDate);
            return {
                ...state,
                shouldRouteKundeToFremtind:
                    kundeHasNoActiveForsikringOnSkadedato &&
                    state.routeToFremtindFeatureToggleEnabled &&
                    isSkadedatoAfterKonvertingStartDate,
            };
        }
        default:
            return {
                ...state,
                sporsmalsflytState: sporsmalsflytReducer(
                    state.sporsmals,
                    state.triggered,
                    state.sporsmalsflytState,
                    action,
                ),
            };
    }
};

export const userHasFilledInInfo = (state: SkademeldingState | KlageState): boolean =>
    state.savingState !== SavingState.Success && !sporsmalsflytStateIsEmpty(state.sporsmalsflytState);
